// V2A synthetic decision contract. This file is NOT connected to Book Workspace.
// No database reads/writes, checkpoint creation, locks, approval, or publication.
// Owner-only persistence and server-verified source/format fingerprints are
// REQUIRED before V2 can be used with real books. A visit is NEVER completion.
const clean = value => String(value ?? "").trim();
const yes = value => value === true;

function verified(checkpoints, key, sourceFingerprint, formatFingerprint, formatBound) {
  const item = checkpoints?.[key];
  if (!item || !yes(item.confirmed) || !sourceFingerprint) return false;
  if (clean(item.sourceFingerprint) !== sourceFingerprint) return false;
  if (formatBound && (!formatFingerprint || clean(item.formatFingerprint) !== formatFingerprint)) return false;
  return true;
}

// Returns a recommendation, NOT permission. The destination must be checked
// independently against its existing owner/pilot access controls.
export function novelContextualJourneyV2({
  publicationStatus = "inDevelopment", chapterCount = 0, sceneCount = 0,
  readiness = {}, checkpoints = {}, sourceFingerprint = "", formatFingerprint = "",
  previewEnabled = false, previewBusy = false, finalApprovalEnabled = false,
} = {}) {
  const status = clean(publicationStatus).toLowerCase();
  const published = ["publishedamazon", "publishedexternal"].includes(status);
  const source = clean(sourceFingerprint), format = clean(formatFingerprint);
  const manuscriptReady = yes(readiness?.manuscriptReady);
  const formatReady = yes(readiness?.formatReady);
  const productionReady = yes(readiness?.readyToPreview);
  const step = (id, reason, options = {}) => ({ id, reason, ...options });

  // External publication status is not a mandate to edit or reapprove a book.
  if (published) return step(null, "already-published", { terminal: true });
  if (!(Number(chapterCount) > 0)) return step("book-structure", "add-chapters");
  if (!(Number(sceneCount) > 0)) return step("write", "add-scenes");
  if (Number(readiness?.unassignedCanonCount) > 0) {
    return step("book-structure", "unassigned-canon-scenes");
  }

  // An author must choose "manuscript ready" expressly. Legacy 'completed'
  // publication metadata, word counts and page visits never substitute for it.
  const manuscriptConfirmed = verified(checkpoints, "manuscript", source, format, false);
  if (!manuscriptConfirmed) {
    return step("write", checkpoints?.manuscript?.confirmed === true
      ? "manuscript-changed-reconfirmation-required"
      : "author-manuscript-confirmation-required", {
      manuscriptConfirmationEligible: manuscriptReady && Boolean(source),
    });
  }
  // A previous confirmation does not conceal empty/unassigned saved scenes.
  if (!manuscriptReady) return step("book-structure", "saved-manuscript-issues");

  const reviewed = verified(checkpoints, "review", source, format, false);
  if (!reviewed) return step("review", "author-review-confirmation-required");

  if (!formatReady) return step("setup", "format-or-covers-incomplete");
  if (!format) return step("setup", "format-verification-unknown");
  if (!productionReady) return step("publication-preparation", "production-readiness-issues");

  const prepared = verified(checkpoints, "preparation", source, format, true);
  if (!prepared) return step("publication-preparation", "author-preparation-confirmation-required");
  const layoutReviewed = verified(checkpoints, "layout", source, format, true);
  if (!layoutReviewed) return step("publication-proof", "author-layout-confirmation-required");

  if (!previewEnabled) return step(null, "private-preview-not-authorized", { restricted: true });
  const privatePreviewReviewed = verified(checkpoints, "preview", source, format, true);
  if (!privatePreviewReviewed) return step("preview-book-interior", "private-preview-review-required", { busy: previewBusy });
  if (!finalApprovalEnabled) return step(null, "final-approval-not-authorized", { restricted: true });
  // Still only an advisory destination: final PDF approval keeps its strict
  // identity, owner, snapshot, acknowledgment and backend enforcement.
  return step("final-print-approval", "restricted-final-approval-checks-required");
}
